<template>
  <div class="exam-search" ref="root">
    <i class="bi bi-search exam-search__icon"></i>
    <input
      ref="input"
      type="text"
      role="combobox"
      aria-autocomplete="list"
      :aria-expanded="isOpen"
      :aria-activedescendant="activeId"
      :value="modelValue"
      v-bind="$attrs"
      class="form-control form-control-solid exam-search__input"
      placeholder="Pesquisar por código TUSS ou nome do exame..."
      autocomplete="off"
      @input="updateValue"
      @keydown.down.prevent="move(1)"
      @keydown.up.prevent="move(-1)"
      @keydown.enter="onEnter"
      @keydown.esc="close"
      @focus="reopen"
    >

    <Teleport to="body">
      <div v-if="isOpen" ref="panel" class="exam-search__panel" :style="panelStyle">
        <ul v-if="exams.length" class="exam-search__list" role="listbox">
          <li
            v-for="(exam, index) in exams"
            :id="optionId(index)"
            :key="exam.id_exam ?? index"
            role="option"
            :aria-selected="index === activeIndex"
            :class="['exam-search__option', { 'is-active': index === activeIndex }]"
            @mouseenter="activeIndex = index"
            @mousedown.prevent="selectExam(exam)"
          >
            <span class="exam-search__name">{{ exam.exam_name }}</span>
            <span v-if="exam.exam_tuss_code" class="exam-search__code">TUSS {{ exam.exam_tuss_code }}</span>
          </li>
        </ul>
        <p v-else-if="loading" class="exam-search__state">Buscando...</p>
        <p v-else class="exam-search__state">Nenhum exame encontrado.</p>
      </div>
    </Teleport>
  </div>
</template>

<script>
import http from "../helpers/http";

const MIN_LENGTH = 2;
const SEARCH_DELAY = 250;

export default {
  name: "InputSearchExam",
  inheritAttrs: false,
  emits: ["onSetSearchExam", "onClearSearchExam", "update:modelValue"],
  props: {
    modelValue: {type: String, default: ''},
  },
  data() {
    return {
      exams: [],
      isOpen: false,
      loading: false,
      activeIndex: -1,
      timer: null,
      sequence: 0,
      panelStyle: {},
    };
  },
  computed: {
    activeId() {
      return this.activeIndex >= 0 ? this.optionId(this.activeIndex) : null;
    },
  },
  mounted() {
    document.addEventListener('mousedown', this.onDocumentClick);
    window.addEventListener('scroll', this.updatePosition, true);
    window.addEventListener('resize', this.updatePosition);
  },
  beforeUnmount() {
    document.removeEventListener('mousedown', this.onDocumentClick);
    window.removeEventListener('scroll', this.updatePosition, true);
    window.removeEventListener('resize', this.updatePosition);
    clearTimeout(this.timer);
  },
  methods: {
    optionId(index) {
      return `exam-search-option-${this.$.uid}-${index}`;
    },
    updateValue(event) {
      const value = event.target.value;
      this.$emit('update:modelValue', value);
      this.$emit('onClearSearchExam');
      this.scheduleSearch(value);
    },
    scheduleSearch(value) {
      clearTimeout(this.timer);
      const search = (value ?? '').trim();

      if (search.length < MIN_LENGTH) {
        this.sequence++;
        this.exams = [];
        this.loading = false;
        this.close();
        return;
      }

      this.loading = true;
      this.open();
      this.timer = setTimeout(() => this.search(search), SEARCH_DELAY);
    },
    search(search) {
      const current = ++this.sequence;

      http.get('/exams', {params: {search}}).then((response) => {
        if (current !== this.sequence) return;

        this.exams = response.data ?? [];
        this.activeIndex = this.exams.length ? 0 : -1;
        this.loading = false;
      }).catch(() => {
        if (current !== this.sequence) return;

        this.exams = [];
        this.loading = false;
      });
    },
    selectExam(exam) {
      const label = [exam.exam_tuss_code, exam.exam_name].filter(Boolean).join(' - ');
      this.$emit('update:modelValue', label);
      this.close();
      this.$emit('onSetSearchExam', exam);
    },
    move(step) {
      if (!this.isOpen || !this.exams.length) return;

      this.activeIndex = (this.activeIndex + step + this.exams.length) % this.exams.length;
      this.$nextTick(() => {
        this.$refs.panel?.querySelector('.exam-search__option.is-active')?.scrollIntoView({block: 'nearest'});
      });
    },
    onEnter(event) {
      if (!this.isOpen || this.activeIndex < 0) return;

      event.preventDefault();
      this.selectExam(this.exams[this.activeIndex]);
    },
    reopen() {
      if (this.exams.length) this.open();
    },
    open() {
      this.isOpen = true;
      this.$nextTick(this.updatePosition);
    },
    close() {
      this.isOpen = false;
      this.activeIndex = -1;
    },
    updatePosition() {
      const input = this.$refs.input;
      if (!this.isOpen || !input) return;

      const rect = input.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < 220 && rect.top > spaceBelow;

      this.panelStyle = openUp
        ? {
          left: `${rect.left}px`,
          width: `${rect.width}px`,
          bottom: `${window.innerHeight - rect.top + 4}px`,
          maxHeight: `${rect.top - 16}px`,
        }
        : {
          left: `${rect.left}px`,
          width: `${rect.width}px`,
          top: `${rect.bottom + 4}px`,
          maxHeight: `${spaceBelow - 16}px`,
        };
    },
    onDocumentClick(event) {
      const inside = this.$refs.root?.contains(event.target) || this.$refs.panel?.contains(event.target);
      if (!inside) this.close();
    },
  },
};
</script>

<style scoped>
.exam-search {
  position: relative;
}

.exam-search__icon {
  position: absolute;
  top: 50%;
  left: 0.9rem;
  z-index: 1;
  color: #7e8299;
  transform: translateY(-50%);
  pointer-events: none;
}

.exam-search__input {
  padding-left: 2.65rem;
}

.exam-search__panel {
  position: fixed;
  z-index: 2300;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e4e6ef;
  border-radius: 0.55rem;
  box-shadow: 0 12px 32px rgba(24, 28, 50, 0.16);
}

.exam-search__list {
  margin: 0;
  padding: 0.25rem;
  overflow-y: auto;
  list-style: none;
}

.exam-search__option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.65rem 0.75rem;
  border-radius: 0.5rem;
  color: #181c32;
  cursor: pointer;
}

.exam-search__option.is-active {
  background: #f8f5f7;
  color: #780f5a;
}

.exam-search__name {
  min-width: 0;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.exam-search__code {
  flex-shrink: 0;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  background: #f1f3f5;
  color: #7e8299;
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
}

.exam-search__state {
  margin: 0;
  padding: 0.75rem 0.9rem;
  color: #7e8299;
  font-size: 0.875rem;
}
</style>
